import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db, analytics } from './firebase';
import { sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, onAuthStateChanged, signOut, deleteUser, verifyBeforeUpdateEmail, updateEmail } from 'firebase/auth';
import { collection, query, where, getDocs, doc, updateDoc, writeBatch, setDoc, getDoc } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';

// --- TEXT TRANSLATIONS (Simple dictionary approach for demo) ---
const translations = {
  'en': {
    searchTitle: "Find Your Next Chapter",
    keywordPH: "Job Title (e.g. Data Scientist)",
    locationPH: "Location (e.g. New York)",
    next: "Next",
    filtersTitle: "Refine Your Search",
    qualifiersTitle: "Qualifying Factors",
    disqualifiersTitle: "Disqualifying Factors",
    qualifierPH: "Add a qualifier (e.g., Remote, $100k+)",
    disqualifierPH: "Add a disqualifier (e.g., Requires German, On-site)",
    addItem: "Add Item",
    removeItem: "Remove",
    searchBtn: "Create Job Alert",
    resultsTitle: "We found these jobs for you",
    alertBtn: "Create Alert for this Search",
    signupTitle: "Sign up to save this alert",
    emailPH: "Email Address",
    signupBtn: "Sign Up & Create Alert",
    loading: "Searching...",
    back: "Back",
    edit: "Edit",
    notifyPrefix: "We will notify you when jobs matching your filters",
    notifySuffix: "appear.",
    cancel: "Cancel",
    myAlerts: "My Alerts",
    search: "Search",
    faqs: "FAQs",
    noAlerts: "No alerts found.",
    delete: "Delete",
    accountSettings: "Account Settings",
    changeEmail: "Change Email",
    update: "Update",
    deleteAccount: "Delete Account",
    currentEmailLabel: "Current email:",
    newEmailLabel: "New email:",
    newEmailPH: "New Email Address",
    signOut: "Sign Out",
    signIn: "Sign In",
    signInTitle: "Sign In",
    signInBtn: "Send Login Link",
    homeIntro: "Jobo.bot alerts you whenever a job posting matches your criteria. Enter the keyword you want to search for, your location, as well as the factors that matter to you. Our AI will send you a daily email with new matches!",
    signedInAlertCreated: "Signed in and alert created successfully.",
    signedInSuccess: "Signed in successfully.",
    errorSigningIn: "Error signing in: {message}",
    alertDeleted: "Alert deleted.",
    emailUpdated: "Email updated successfully.",
    errorUpdatingEmail: "Error updating email: {message}",
    accountDeleted: "Account deleted.",
    errorDeletingAccount: "Error deleting account: {message}",
    alertCreated: "Alert created successfully.",
    errorCreatingAlert: "Error creating alert: {message}",
    loginLinkSent: "Login link sent to {email}. Check your inbox.",
    genericError: "Error: {message}",
    maxAlertsReached: "You can have up to 3 alerts per account."
    ,
    enabledLabel: "Enabled",
    checkEmailTitle: "Check Your Email",
    checkEmailMessage: "We've sent a sign-in link to {email}. Click the link in the email to complete your signup and create your job alert.",
    checkEmailNote: "The link will expire in a few minutes. If you don't see the email, check your spam folder.",
    alertUpdated: "Alert updated.",
    emailVerificationSent: "Verification link sent to {email}. Check your inbox.",
    reauthRequired: "Please sign in again to update your email.",
    reauthLinkSent: "Reauthentication link sent to {email}. Check your inbox.",
    confirmDeleteTitle: "Delete Alert",
    confirmDeleteMessage: "Are you sure you want to delete this alert?",
    confirmDeleteAccountTitle: "Delete Account",
    confirmDeleteAccountMessage: "Are you sure? This action cannot be undone.",
    pageTitle: "Jobo.bot - Find Your Next Chapter",
    unsubscribeSuccess: "All your job alerts have been disabled successfully.",
    unsubscribeError: "Error disabling alerts: {message}",
    unsubscribeNoToken: "Invalid unsubscribe link.",
    returnToHomePage: "Return to home page",
    latestMatches: "Latest Matches",
    noMatches: "No matches found yet.",
    matchReason: "Match reason:",
    salary: "Salary:",
    remote: "Remote",
    viewJob: "View Job",
    loadingMatches: "Loading matches...",
    close: "Close",
    seenMinute: "Seen 1 minute ago",
    seenMinutes: "Seen {count} minutes ago",
    seenHour: "Seen 1 hour ago",
    seenHours: "Seen {count} hours ago",
    seenDay: "Seen 1 day ago",
    seenDays: "Seen {count} days ago",
    seenJustNow: "Seen just now",
    privacyPolicy: "Privacy Policy",
    termsOfUse: "Terms of Use",
    cookieConsent: "We use essential cookies to maintain your session. By continuing, you accept our",
    cookieConsentAnd: "and",
    cookieAccept: "Accept",
    showAllResults: "Show all results",
    selectLocationFromList: "Please select a location from the suggestions",
    notMatchReason: "Not a match because:",
    emailConsent: "By creating this alert, I give jobo.bot permission to send me emails related to my account and new job alerts",
    marketingConsent: "I would like to receive marketing emails about new features and tips",
    marketingPrompt: "Would you like to receive marketing emails about new features and tips?",
    marketingPromptYes: "Yes, please",
    marketingPromptNo: "No, thanks",
    faqsTitle: "Frequently Asked Questions",
    faq1Question: "How does jobo.bot work?",
    faq1Answer: "jobo.bot is an automated service that crawls job listings from various sources across the web. We match jobs based on your preferences (keywords, location, qualifiers, and disqualifiers) and send you email alerts when new matching jobs are found.",
    faq2Question: "How many alerts can I create?",
    faq2Answer: "You can create up to 3 job alerts per account. Each alert can have different search criteria to help you find the perfect job.",
    faq3Question: "How often will I receive email alerts?",
    faq3Answer: "You'll receive email alerts daily, as long as new jobs matching your criteria are found.",
    faq4Question: "Can I edit or delete my alerts?",
    faq4Answer: "Yes! You can edit your alerts by clicking the edit button, or delete them if you no longer need them. You can also temporarily disable alerts without deleting them.",
    faq5Question: "How do I unsubscribe?",
    faq5Answer: "You can unsubscribe by clicking the 'Unsubscribe' link at the bottom of any email we send you, or by deleting all your alerts from the 'My Alerts' page.",
    faq6Question: "Is my data secure?",
    faq6Answer: "Yes, we take your privacy seriously. Your data is stored securely and we never sell your information to third parties. See our Privacy Policy for more details."
  },
  'es': {
    searchTitle: "Encuentra tu próximo capítulo",
    keywordPH: "Título (ej. Data Scientist)",
    locationPH: "Ubicación (ej. Madrid)",
    next: "Siguiente",
    filtersTitle: "Refina tu búsqueda",
    qualifiersTitle: "Incluir ofertas que",
    disqualifiersTitle: "Excluir ofertas que",
    qualifierPH: "Añade un calificador (ej. Remoto, $100k+)",
    disqualifierPH: "Añade un descalificador (ej. Pide alemán, Presencial)",
    addItem: "Añadir",
    removeItem: "Eliminar",
    searchBtn: "Crear alerta de empleo",
    resultsTitle: "Encontramos estos empleos",
    alertBtn: "Crear alerta",
    signupTitle: "Regístrate para guardar la alerta",
    emailPH: "Correo electrónico",
    signupBtn: "Registrarse y crear",
    loading: "Buscando...",
    back: "Atrás",
    edit: "Editar",
    notifyPrefix: "Te notificaremos cuando aparezcan empleos que coincidan con tus filtros",
    notifySuffix: ".",
    cancel: "Cancelar",
    myAlerts: "Mis alertas",
    search: "Buscar",
    noAlerts: "No se encontraron alertas.",
    delete: "Eliminar",
    accountSettings: "Configuración de cuenta",
    changeEmail: "Cambiar email",
    update: "Guardar",
    deleteAccount: "Eliminar cuenta",
    currentEmailLabel: "Email actual:",
    newEmailLabel: "Nuevo email:",
    newEmailPH: "Nuevo correo electrónico",
    signOut: "Cerrar sesión",
    signIn: "Iniciar sesión",
    signInTitle: "Iniciar sesión",
    signInBtn: "Enviar enlace de acceso",
    homeIntro: "Introduce la palabra clave por la que quieres buscar, tu ubicación, así como las características para buscar y evitar. ¡Nuestra IA te enviará un email diario con las nuevas coincidencias!",
    signedInAlertCreated: "Has iniciado sesión y la alerta fue creada correctamente.",
    signedInSuccess: "Inicio de sesión correcto.",
    errorSigningIn: "Error al iniciar sesión: {message}",
    alertDeleted: "Alerta eliminada.",
    emailUpdated: "Email actualizado correctamente.",
    errorUpdatingEmail: "Error al actualizar el email: {message}",
    accountDeleted: "Cuenta eliminada.",
    errorDeletingAccount: "Error al eliminar la cuenta: {message}",
    alertCreated: "Alerta creada correctamente.",
    errorCreatingAlert: "Error al crear la alerta: {message}",
    loginLinkSent: "Enlace de acceso enviado a {email}. Revisa tu bandeja.",
    genericError: "Error: {message}",
    maxAlertsReached: "Puedes tener hasta 3 alertas por cuenta."
    ,
    enabledLabel: "Activada",
    checkEmailTitle: "Revisa tu email",
    checkEmailMessage: "Hemos enviado un enlace de acceso a {email}. Haz clic en el enlace del email para completar tu registro y crear tu alerta de empleo.",
    checkEmailNote: "El enlace expirará en unos minutos. Si no ves el email, revisa tu carpeta de spam.",
    alertUpdated: "Alerta actualizada.",
    emailVerificationSent: "Hemos enviado un enlace de verificación a {email}. Revisa tu bandeja.",
    reauthRequired: "Por favor, vuelve a iniciar sesión para actualizar tu email.",
    reauthLinkSent: "Enlace de reautenticación enviado a {email}. Revisa tu bandeja.",
    confirmDeleteTitle: "Eliminar alerta",
    confirmDeleteMessage: "¿Seguro que quieres eliminar esta alerta?",
    confirmDeleteAccountTitle: "Eliminar cuenta",
    confirmDeleteAccountMessage: "¿Estás seguro? Esta acción no se puede deshacer.",
    pageTitle: "Jobo.bot - Encuentra tu próximo capítulo",
    unsubscribeSuccess: "Todas tus alertas de empleo han sido desactivadas correctamente.",
    unsubscribeError: "Error al desactivar las alertas: {message}",
    unsubscribeNoToken: "Enlace de cancelación de suscripción inválido.",
    returnToHomePage: "Volver a la página principal",
    latestMatches: "Últimas coincidencias",
    noMatches: "Aún no se encontraron coincidencias.",
    matchReason: "Razón de coincidencia:",
    salary: "Salario:",
    remote: "Remoto",
    viewJob: "Ver oferta",
    loadingMatches: "Cargando coincidencias...",
    close: "Cerrar",
    seenMinute: "Visto hace 1 minuto",
    seenMinutes: "Visto hace {count} minutos",
    seenHour: "Visto hace 1 hora",
    seenHours: "Visto hace {count} horas",
    seenDay: "Visto hace 1 día",
    seenDays: "Visto hace {count} días",
    seenJustNow: "Visto hace un momento",
    privacyPolicy: "Política de privacidad",
    termsOfUse: "Términos de uso",
    cookieConsent: "Usamos cookies esenciales para mantener tu sesión. Al continuar, aceptas nuestra",
    cookieConsentAnd: "y",
    cookieAccept: "Aceptar",
    showAllResults: "Mostrar todos los resultados",
    selectLocationFromList: "Por favor, selecciona una ubicación de las sugerencias",
    notMatchReason: "No coincide porque:",
    emailConsent: "Al crear esta alerta, doy permiso a jobo.bot para enviarme correos electrónicos relacionados con mi cuenta y nuevas alertas de empleo",
    marketingConsent: "Me gustaría recibir correos de marketing sobre nuevas funciones y consejos",
    marketingPrompt: "¿Te gustaría recibir correos de marketing sobre nuevas funciones y consejos?",
    marketingPromptYes: "Sí, por favor",
    marketingPromptNo: "No, gracias",
    faqs: "FAQs",
    faqsTitle: "Preguntas Frecuentes",
    faq1Question: "¿Cómo funciona jobo.bot?",
    faq1Answer: "jobo.bot es un servicio automatizado que rastrea ofertas de empleo de diversas fuentes en la web. Emparejamos trabajos según tus preferencias (palabras clave, ubicación, calificadores y descalificadores) y te enviamos alertas por correo cuando se encuentran nuevos trabajos que coinciden.",
    faq2Question: "¿Cuántas alertas puedo crear?",
    faq2Answer: "Puedes crear hasta 3 alertas de empleo por cuenta. Cada alerta puede tener diferentes criterios de búsqueda para ayudarte a encontrar el trabajo perfecto.",
    faq3Question: "¿Con qué frecuencia recibiré alertas por correo?",
    faq3Answer: "Recibirás alertas por correo todos los días, siempre que se encuentren nuevos trabajos que coincidan con tus criterios. La frecuencia depende de cuántos trabajos coincidentes se publiquen.",
    faq4Question: "¿Puedo editar o eliminar mis alertas?",
    faq4Answer: "¡Sí! Puedes editar tus alertas haciendo clic en el botón de editar, o eliminarlas si ya no las necesitas. También puedes desactivar temporalmente las alertas sin eliminarlas.",
    faq5Question: "¿Cómo me doy de baja?",
    faq5Answer: "Puedes darte de baja haciendo clic en el enlace 'Darse de baja' al final de cualquier correo que te enviemos, o eliminando todas tus alertas desde la página 'Mis alertas'.",
    faq6Question: "¿Están seguros mis datos?",
    faq6Answer: "Sí, nos tomamos tu privacidad muy en serio. Tus datos se almacenan de forma segura y nunca vendemos tu información a terceros. Consulta nuestra Política de Privacidad para más detalles."
  }
}

// Capture LinkedIn click ID on landing (before React mounts)
const LI_FAT_ID_KEY = 'li_fat_id';
(() => {
  const params = new URLSearchParams(window.location.search);
  const liFatId = params.get('li_fat_id');
  if (liFatId) window.localStorage.setItem(LI_FAT_ID_KEY, liFatId);
})();

const App = () => {
  const [step, setStep] = useState(1); // 1: Landing, 2: Requisites, 3: Results
  const [showSignup, setShowSignup] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailLinkSent, setEmailLinkSent] = useState(false);
  const [emailForConfirmation, setEmailForConfirmation] = useState('');

  // Data State
  const [formData, setFormData] = useState({ keyword: '', location: '', qualifiers: [], disqualifiers: [] });
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showMarketingPrompt, setShowMarketingPrompt] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationSearchTimeout, setLocationSearchTimeout] = useState(null);
  const [isValidLocationSelected, setIsValidLocationSelected] = useState(false);
  const locationInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Google Maps Autocomplete Service
  const autocompleteService = useRef(null);
  const sessionToken = useRef(null);

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places && !autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  }, []);

  const fetchGooglePredictions = (query, langCode, callback) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) return;

    if (!autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }

    if (!sessionToken.current) {
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }

    autocompleteService.current.getPlacePredictions({
      input: query,
      types: ['(regions)'], // Updated to match user request which changed this to regions
      language: langCode || 'en',
      sessionToken: sessionToken.current
    }, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        const formatted = predictions.map(p => ({
          description: p.description,
          mainText: p.structured_formatting.main_text,
          secondaryText: p.structured_formatting.secondary_text,
          place_id: p.place_id
        }));
        callback(formatted);
      } else {
        callback([]);
      }
    });
  };
  // Removed unused jobs state
  const [user, setUser] = useState(null);
  const [userAlerts, setUserAlerts] = useState([]);
  // Initialize activeTab from URL
  const getInitialTab = () => {
    const path = window.location.pathname;
    if (path.startsWith('/alerts')) return 'alerts';
    if (path === '/unsubscribe') return 'unsubscribe';
    if (path === '/faqs') return 'faqs';
    return 'search';
  };

  const getInitialAlertId = () => {
    const path = window.location.pathname;
    const match = path.match(/^\/alerts\/([^/]+)$/);
    return match ? match[1] : null;
  };

  const [activeTab, setActiveTab] = useState(getInitialTab()); // 'search', 'alerts', or 'unsubscribe'
  const [unsubscribeStatus, setUnsubscribeStatus] = useState(null); // 'loading', 'success', 'error'
  const [selectedAlertId, setSelectedAlertId] = useState(getInitialAlertId());
  const [alertMatches, setAlertMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [showAllResults, setShowAllResults] = useState(false);

  // Cookie Consent State
  const [showCookieConsent, setShowCookieConsent] = useState(false);

  // Language State
  const [lang, setLang] = useState(navigator.language.startsWith('es') ? 'es' : 'en');
  const t = translations[lang];

  // Update page title when language changes
  useEffect(() => {
    document.title = t.pageTitle;
  }, [t.pageTitle]);

  // Close location dropdown when clicking outside and update position on scroll/resize
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showLocationDropdown && !e.target.closest('.location-autocomplete')) {
        setShowLocationDropdown(false);
      }
    };

    const handleScrollResize = () => {
      if (showLocationDropdown) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollResize, true);
    window.addEventListener('resize', handleScrollResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollResize, true);
      window.removeEventListener('resize', handleScrollResize);
      // Cleanup timeout on unmount
      if (locationSearchTimeout) {
        clearTimeout(locationSearchTimeout);
      }
    };
  }, [showLocationDropdown, locationSearchTimeout]);

  // Check if we should show cookie consent (EU users only)
  useEffect(() => {
    const hasConsent = localStorage.getItem('cookieConsent');
    if (hasConsent) {
      setShowCookieConsent(false);
      return;
    }

    // Check if user is likely in EU based on timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const euTimezones = [
      'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
      'Europe/Rome', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna',
      'Europe/Warsaw', 'Europe/Prague', 'Europe/Budapest', 'Europe/Bucharest',
      'Europe/Athens', 'Europe/Stockholm', 'Europe/Copenhagen', 'Europe/Helsinki',
      'Europe/Oslo', 'Europe/Dublin', 'Europe/Lisbon', 'Europe/Sofia',
      'Europe/Zagreb', 'Europe/Luxembourg', 'Europe/Vilnius', 'Europe/Riga',
      'Europe/Tallinn', 'Europe/Ljubljana', 'Europe/Bratislava', 'Europe/Valletta',
      'Europe/Nicosia', 'Atlantic/Azores', 'Atlantic/Madeira', 'Atlantic/Canary'
    ];

    if (euTimezones.some(tz => timezone.includes(tz.split('/')[1]))) {
      setShowCookieConsent(true);
    }
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem('cookieConsent', 'true');
    setShowCookieConsent(false);
  };

  const handleMarketingPromptYes = async () => {
    if (user) {
      setShowMarketingPrompt(false);
      await saveMarketingConsent(user.uid, true);
      if (analytics) {
        logEvent(analytics, 'marketing_consent', { consent: true });
      }
    }
  };

  const handleMarketingPromptNo = async () => {
    if (user) {
      setShowMarketingPrompt(false);
      await saveMarketingConsent(user.uid, false);
      if (analytics) {
        logEvent(analytics, 'marketing_consent', { consent: false });
      }
    }
  };

  // Notifications (toasts)
  const [notifications, setNotifications] = useState([]);
  const addNotification = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const fetchUserAlerts = useCallback(async (userObj) => {
    if (!userObj) return;
    const alertsMap = new Map();
    // Prefer uid-based ownership
    try {
      const qUid = query(collection(db, "alerts"), where("uid", "==", userObj.uid));
      const uidSnap = await getDocs(qUid);
      uidSnap.forEach((d) => {
        alertsMap.set(d.id, { id: d.id, ...d.data() });
      });
    } catch (err) {
      console.warn('Read alerts by uid failed:', err?.message || err);
      addNotification('error', t.genericError.replace('{message}', 'Unable to read alerts.'));
    }
    // No email-based reads; alerts are keyed solely to uid
    const alerts = Array.from(alertsMap.values());
    // Sort so enabled alerts appear first, disabled at the bottom
    alerts.sort((a, b) => {
      const aEnabled = a.enabled !== false;
      const bEnabled = b.enabled !== false;
      if (aEnabled === bEnabled) return 0;
      return aEnabled ? -1 : 1;
    });
    setUserAlerts(alerts);
  }, [addNotification, t.genericError]);

  const saveMarketingConsent = async (uid, consent) => {
    try {
      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, {
        marketingConsent: consent,
        updatedAt: new Date()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving marketing consent:', error);
    }
  };

  const loadMarketingConsent = async (uid) => {
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        // Return null if marketingConsent is not set (hasn't been asked yet)
        // Return true/false if it has been explicitly set
        return userData.hasOwnProperty('marketingConsent') ? userData.marketingConsent : null;
      }
    } catch (error) {
      console.error('Error loading marketing consent:', error);
    }
    return null; // Return null if document doesn't exist or error occurred
  };

  // Update URL when activeTab changes
  const updateUrl = useCallback((tab, alertId = null) => {
    let newPath = '/';
    if (tab === 'alerts' && alertId) newPath = `/alerts/${alertId}`;
    else if (tab === 'alerts') newPath = '/alerts';
    else if (tab === 'unsubscribe') newPath = '/unsubscribe';
    else if (tab === 'faqs') newPath = '/faqs';
    if (window.location.pathname !== newPath) {
      window.history.pushState({ tab, alertId }, '', newPath);
    }
  }, []);

  const fetchAlertMatches = useCallback(async (alertId, showAll = false) => {
    if (!user || !alertId) return;
    setLoadingMatches(true);
    try {
      const constraints = [
        where("alert_id", "==", alertId),
        where("uid", "==", user.uid)
      ];

      if (!showAll) {
        constraints.push(where("match", "==", true));
      }

      const qResults = query(
        collection(db, "results"),
        ...constraints
      );
      const resultsSnapshot = await getDocs(qResults);
      const matches = resultsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by extractedAt descending (most recent first)
      matches.sort((a, b) => {
        const aTime = a.extractedAt?.toMillis?.() || 0;
        const bTime = b.extractedAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      setAlertMatches(matches);
    } catch (error) {
      console.error('Error fetching matches:', error);
      addNotification('error', t.genericError.replace('{message}', error.message || 'Failed to load matches'));
      setAlertMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  }, [user, addNotification, t.genericError]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      const path = window.location.pathname;
      let tab = 'search';
      let alertId = null;

      const alertMatch = path.match(/^\/alerts\/([^/]+)$/);
      if (alertMatch) {
        tab = 'alerts';
        alertId = alertMatch[1];
      } else if (path === '/alerts' || path.startsWith('/alerts')) {
        tab = 'alerts';
      } else if (path === '/unsubscribe') {
        tab = 'unsubscribe';
      } else if (path === '/faqs') {
        tab = 'faqs';
      }

      setActiveTab(tab);
      setSelectedAlertId(alertId);
      if (alertId) {
        fetchAlertMatches(alertId);
      } else {
        setAlertMatches([]);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [fetchAlertMatches]);

  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  // Sync URL when activeTab or selectedAlertId changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Don't update URL on initial mount, it's already correct
    }
    updateUrl(activeTab, selectedAlertId);
  }, [activeTab, selectedAlertId, updateUrl]);

  // Track if auth state has been determined
  const [authChecked, setAuthChecked] = useState(false);

  // Redirect to search if user tries to access /alerts without being logged in
  // Only redirect after auth state has been checked
  useEffect(() => {
    if (authChecked && activeTab === 'alerts' && !user) {
      setActiveTab('search');
      window.history.replaceState({ tab: 'search' }, '', '/');
    }
  }, [activeTab, user, authChecked]);

  // Fetch matches on initial load if there's an alert_id in the URL
  const initialAlertIdRef = useRef(getInitialAlertId());
  const hasFetchedInitialMatches = useRef(false);

  useEffect(() => {
    if (
      !hasFetchedInitialMatches.current &&
      initialAlertIdRef.current &&
      user &&
      authChecked &&
      activeTab === 'alerts'
    ) {
      hasFetchedInitialMatches.current = true;
      fetchAlertMatches(initialAlertIdRef.current);
    }
  }, [user, authChecked, activeTab, fetchAlertMatches]);

  const toggleLanguage = () => {
    const newLang = lang === 'en' ? 'es' : 'en';
    setLang(newLang);
    if (analytics) {
      logEvent(analytics, 'language_changed', { language: newLang });
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (analytics) {
      logEvent(analytics, 'tab_changed', { tab });
    }
  };

  // Remove apikey/apiKey and other Firebase-related query params
  const cleanUrlParams = useCallback(() => {
    const url = new URL(window.location.href);
    let hasChanges = false;

    // Remove apikey in various cases
    if (url.searchParams.has('apikey')) {
      url.searchParams.delete('apikey');
      hasChanges = true;
    }
    if (url.searchParams.has('apiKey')) {
      url.searchParams.delete('apiKey');
      hasChanges = true;
    }

    // Remove Firebase email link params (oobCode, mode, continueUrl, etc.)
    const firebaseParams = ['oobCode', 'mode', 'continueUrl', 'lang', 'pendingAlert'];
    firebaseParams.forEach(param => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // --- HANDLERS ---

  // Handle unsubscribe route
  useEffect(() => {
    if (activeTab === 'unsubscribe' && !handledUnsubscribeRef.current) {
      handledUnsubscribeRef.current = true;
      const urlParams = new URLSearchParams(window.location.search);
      const uid = urlParams.get('uid');
      const sig = urlParams.get('sig');

      if (!uid || !sig) {
        // Missing required parameters, redirect to home
        setActiveTab('search');
        window.history.replaceState({ tab: 'search' }, '', '/');
        addNotification('error', t.unsubscribeNoToken);
        return;
      }

      // Both parameters provided, call unsubscribe endpoint
      setUnsubscribeStatus('loading');
      fetch('https://europe-west1-jobobot-1.cloudfunctions.net/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uid, sig })
      })
        .then(async (response) => {
          const data = await response.json();
          if (response.ok && data && data.success) {
            setUnsubscribeStatus('success');
            addNotification('success', t.unsubscribeSuccess);
            // Clean URL params
            cleanUrlParams();
            if (analytics) {
              logEvent(analytics, 'unsubscribe', { uid, alerts_disabled: data.count || 0 });
            }
          } else {
            setUnsubscribeStatus('error');
            addNotification('error', t.unsubscribeError.replace('{message}', data?.message || 'Invalid response from server'));
          }
        })
        .catch((error) => {
          setUnsubscribeStatus('error');
          addNotification('error', t.unsubscribeError.replace('{message}', error.message || 'Unknown error'));
        });
    } else if (activeTab !== 'unsubscribe') {
      // Reset the ref when we're not on the unsubscribe page
      handledUnsubscribeRef.current = false;
    }
  }, [activeTab, t.unsubscribeSuccess, t.unsubscribeError, t.unsubscribeNoToken, addNotification, cleanUrlParams]);

  // Confirm modal
  const [confirmState, setConfirmState] = useState({ open: false, title: '', message: '', onConfirm: null });
  const openConfirm = (title, message, onConfirm) => {
    setConfirmState({ open: true, title, message, onConfirm });
  };
  const closeConfirm = () => {
    setConfirmState({ open: false, title: '', message: '', onConfirm: null });
  };

  // Edit Alert Modal State
  const [editState, setEditState] = useState({
    open: false,
    alert: null,
    keyword: '',
    location: '',
    qualifiers: [],
    disqualifiers: []
  });
  const [editLocationSuggestions, setEditLocationSuggestions] = useState([]);
  const [showEditLocationDropdown, setShowEditLocationDropdown] = useState(false);
  const [editLocationSearchTimeout, setEditLocationSearchTimeout] = useState(null);
  const [isEditValidLocationSelected, setIsEditValidLocationSelected] = useState(false);
  const editLocationInputRef = useRef(null);

  const openEdit = (alert) => {
    setEditState({
      open: true,
      alert,
      keyword: alert.keyword || '',
      location: alert.location || '',
      qualifiers: Array.isArray(alert.qualifiers) ? [...alert.qualifiers] : [],
      disqualifiers: Array.isArray(alert.disqualifiers) ? [...alert.disqualifiers] : []
    });
    setIsEditValidLocationSelected(true); // Existing location is valid
    setEditLocationSuggestions([]);
    setShowEditLocationDropdown(false);
  };

  const closeEdit = () => {
    setEditState({ open: false, alert: null, keyword: '', location: '', qualifiers: [], disqualifiers: [] });
    setEditLocationSuggestions([]);
    setShowEditLocationDropdown(false);
    setIsEditValidLocationSelected(false);
    if (editLocationSearchTimeout) {
      clearTimeout(editLocationSearchTimeout);
    }
  };

  const updateEditField = (field, value) => {
    setEditState(prev => ({ ...prev, [field]: value }));
  };

  const addEditQualifier = () => {
    setEditState(prev => ({ ...prev, qualifiers: [...prev.qualifiers, ''] }));
  };

  const updateEditQualifier = (index, value) => {
    setEditState(prev => ({
      ...prev,
      qualifiers: prev.qualifiers.map((q, i) => (i === index ? value : q))
    }));
  };

  const removeEditQualifier = (index) => {
    setEditState(prev => ({
      ...prev,
      qualifiers: prev.qualifiers.filter((_, i) => i !== index)
    }));
  };

  const addEditDisqualifier = () => {
    setEditState(prev => ({ ...prev, disqualifiers: [...prev.disqualifiers, ''] }));
  };

  const updateEditDisqualifier = (index, value) => {
    setEditState(prev => ({
      ...prev,
      disqualifiers: prev.disqualifiers.map((d, i) => (i === index ? value : d))
    }));
  };

  const removeEditDisqualifier = (index) => {
    setEditState(prev => ({
      ...prev,
      disqualifiers: prev.disqualifiers.filter((_, i) => i !== index)
    }));
  };

  const handleEditLocationInput = (e) => {
    const value = e.target.value;
    setEditState(prev => ({ ...prev, location: value }));
    setIsEditValidLocationSelected(false);

    // Clear previous timeout
    if (editLocationSearchTimeout) {
      clearTimeout(editLocationSearchTimeout);
    }

    // Debounce the API call
    const timeout = setTimeout(() => {
      fetchEditLocationSuggestions(value);
    }, 300);

    setEditLocationSearchTimeout(timeout);
  };

  const fetchEditLocationSuggestions = (query) => {
    if (!query || query.length < 2) {
      setEditLocationSuggestions([]);
      setShowEditLocationDropdown(false);
      return;
    }

    fetchGooglePredictions(query, lang, (results) => {
      setEditLocationSuggestions(results);
      if (results.length > 0) {
        setShowEditLocationDropdown(true);
      } else {
        setShowEditLocationDropdown(false);
      }
    });
  };

  const handleEditLocationSelect = (suggestion) => {
    setEditState(prev => ({ ...prev, location: suggestion.description }));
    setIsEditValidLocationSelected(true);
    setShowEditLocationDropdown(false);
    setEditLocationSuggestions([]);

    // Reset session token on selection
    sessionToken.current = null;
  };

  const handleSaveEditedAlert = async () => {
    if (!editState.alert) return;

    // Validate location selection
    if (!isEditValidLocationSelected && editState.location) {
      addNotification('error', t.selectLocationFromList);
      return;
    }

    const updated = {
      keyword: (editState.keyword || '').trim(),
      location: (editState.location || '').trim(),
      qualifiers: (editState.qualifiers || []).map(v => (v || '').trim()).filter(Boolean),
      disqualifiers: (editState.disqualifiers || []).map(v => (v || '').trim()).filter(Boolean)
    };
    try {
      // Persist to Firestore
      await updateDoc(doc(db, 'alerts', editState.alert.id), updated);
      // Update local list
      setUserAlerts(prev => prev.map(a => (
        a.id === editState.alert.id ? { ...a, ...updated } : a
      )));
      addNotification('success', t.alertUpdated);
      closeEdit();
    } catch (error) {
      addNotification('error', t.genericError.replace('{message}', error.message || ''));
    }
  };

  const addQualifier = () => {
    setFormData(prev => ({ ...prev, qualifiers: [...prev.qualifiers, ''] }));
  };

  const updateQualifier = (index, value) => {
    setFormData(prev => ({
      ...prev,
      qualifiers: prev.qualifiers.map((q, i) => (i === index ? value : q))
    }));
  };

  const removeQualifier = (index) => {
    setFormData(prev => ({
      ...prev,
      qualifiers: prev.qualifiers.filter((_, i) => i !== index)
    }));
  };

  const addDisqualifier = () => {
    setFormData(prev => ({ ...prev, disqualifiers: [...prev.disqualifiers, ''] }));
  };

  const updateDisqualifier = (index, value) => {
    setFormData(prev => ({
      ...prev,
      disqualifiers: prev.disqualifiers.map((d, i) => (i === index ? value : d))
    }));
  };

  const removeDisqualifier = (index) => {
    setFormData(prev => ({
      ...prev,
      disqualifiers: prev.disqualifiers.filter((_, i) => i !== index)
    }));
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setAuthChecked(true); // Mark auth as checked
      if (currentUser) {
        fetchUserAlerts(currentUser);

        // Load marketing consent preference from DB
        const consent = await loadMarketingConsent(currentUser.uid);
        // Show prompt if user hasn't set a preference (null means not asked yet)
        if (consent === null) {
          setShowMarketingPrompt(true);
        }

        // Track sign-in time if we don't have one yet (e.g., on page refresh)
        if (!lastSignInTimeRef.current && currentUser.metadata?.lastSignInTime) {
          lastSignInTimeRef.current = new Date(currentUser.metadata.lastSignInTime).getTime();
        }
        // Clean URL params when user is detected (in case email link sign-in already completed)
        setTimeout(() => cleanUrlParams(), 100);
      } else {
        setUserAlerts([]);
        setShowMarketingPrompt(false);
        lastSignInTimeRef.current = null;
      }
    });
    return () => unsubscribe();
  }, [fetchUserAlerts, cleanUrlParams]);

  const handledEmailLinkRef = useRef(false);
  const lastSignInTimeRef = useRef(null);
  const handledUnsubscribeRef = useRef(false);

  useEffect(() => {
    if (handledEmailLinkRef.current) return;
    if (isSignInWithEmailLink(auth, window.location.href)) {
      handledEmailLinkRef.current = true;
      const urlParams = new URLSearchParams(window.location.search);
      let email = urlParams.get('email') || window.localStorage.getItem('emailForSignIn');
      if (!email) {
        email = window.prompt('Please provide your email for confirmation');
      }
      signInWithEmailLink(auth, email, window.location.href)
        .then(async (result) => {
          window.localStorage.removeItem('emailForSignIn');

          // Write LinkedIn click ID to user doc for conversion tracking
          const liFatId = urlParams.get('li_fat_id') || window.localStorage.getItem(LI_FAT_ID_KEY);
          if (liFatId) {
            setDoc(doc(db, 'users', result.user.uid), { li_fat_id: liFatId }, { merge: true }).catch(() => {});
            window.localStorage.removeItem(LI_FAT_ID_KEY);
          }

          // Check for pending alert to save (URL param first, localStorage fallback)
          const urlAlertData = urlParams.get('pendingAlert');
          const pendingAlertRaw = urlAlertData
            ? atob(urlAlertData)
            : window.localStorage.getItem('pendingAlert');
          if (pendingAlertRaw) {
            const alertData = JSON.parse(pendingAlertRaw);
            // Filter out empty strings from qualifiers and disqualifiers
            alertData.qualifiers = (alertData.qualifiers || []).filter(q => q && q.trim() !== '');
            alertData.disqualifiers = (alertData.disqualifiers || []).filter(d => d && d.trim() !== '');
            // Remove marketingConsent from alertData (it belongs in users collection, not alerts)
            delete alertData.marketingConsent;
            // Try to count existing alerts for this user before creating (best effort)
            let canCreate = true;
            try {
              const qAlerts = query(collection(db, "alerts"), where("uid", "==", result.user.uid));
              const existing = await getDocs(qAlerts);
              if (existing.size >= 3) {
                canCreate = false;
              }
            } catch (countErr) {
              // If read fails due to rules, proceed; server-side will guard counts
              console.warn('Alert count read failed:', countErr?.message || countErr);
            }
            if (!canCreate) {
              addNotification('error', t.maxAlertsReached);
              window.localStorage.removeItem('pendingAlert');
            } else {
              const batch = writeBatch(db);
              const alertRef = doc(collection(db, 'alerts'));
              batch.set(alertRef, {
                uid: result.user.uid,
                ...alertData,
                enabled: true,
                createdAt: new Date()
              });
              await batch.commit();
              window.localStorage.removeItem('pendingAlert');

              // Log alert creation event
              if (analytics) {
                logEvent(analytics, 'alert_created', {
                  keyword: alertData.keyword,
                  location: alertData.location,
                  qualifiers_count: (alertData.qualifiers || []).length,
                  disqualifiers_count: (alertData.disqualifiers || []).length,
                  language: alertData.language || 'en'
                });
              }

              // Wait for alerts to be fetched before switching tabs
              await fetchUserAlerts(result.user);
              addNotification('success', t.signedInAlertCreated);
              if (analytics) {
                logEvent(analytics, 'sign_in', { method: 'email_link' });
              }
              // Reset search form so returning to home shows a clean slate
              setFormData({ keyword: '', location: '', qualifiers: [], disqualifiers: [] });
              setIsValidLocationSelected(false);
              setStep(1);
              setEmailLinkSent(false);
              setEmailForConfirmation('');
              setActiveTab('alerts');
              // Clean URL params with a small delay to ensure Firebase has finished processing
              setTimeout(() => cleanUrlParams(), 100);
              // Track sign-in time for email updates
              lastSignInTimeRef.current = Date.now();
            }
          } else {
            // Wait for alerts to be fetched before switching tabs
            await fetchUserAlerts(result.user);
            addNotification('success', t.signedInSuccess);
            if (analytics) {
              logEvent(analytics, 'sign_in', { method: 'email_link' });
            }
            setEmailLinkSent(false);
            setEmailForConfirmation('');
            setActiveTab('alerts');
            // Clean URL params with a small delay to ensure Firebase has finished processing
            setTimeout(() => cleanUrlParams(), 100);
            // Track sign-in time for email updates
            lastSignInTimeRef.current = Date.now();
          }
        })
        .catch((error) => {
          addNotification('error', t.errorSigningIn.replace('{message}', error.message));
        });
    }
  }, [t.signedInAlertCreated, t.signedInSuccess, t.errorSigningIn, t.maxAlertsReached, addNotification, cleanUrlParams, fetchUserAlerts]);

  const handleAlertClick = (alertId) => {
    setShowAllResults(false);
    if (selectedAlertId === alertId || alertId === null) {
      // Close the modal
      setSelectedAlertId(null);
      setAlertMatches([]);
    } else {
      // Open the modal for this alert
      setSelectedAlertId(alertId);
      fetchAlertMatches(alertId, false);
      if (analytics) {
        logEvent(analytics, 'alert_viewed', { alert_id: alertId });
      }
    }
  };

  const handleDeleteAlert = async (alertId) => {
    openConfirm(t.confirmDeleteTitle, t.confirmDeleteMessage, async () => {
      try {
        const batch = writeBatch(db);
        const alertRef = doc(db, 'alerts', alertId);
        batch.delete(alertRef);
        await batch.commit();
        fetchUserAlerts(user);
        addNotification('success', t.alertDeleted);
        if (analytics) {
          logEvent(analytics, 'alert_deleted', { alert_id: alertId });
        }
      } catch (error) {
        addNotification('error', t.genericError.replace('{message}', error.message));
      }
    });
  };

  const toggleAlertEnabled = async (alert) => {
    try {
      const currentlyEnabled = alert.enabled !== false;
      const nextEnabled = !currentlyEnabled;
      await updateDoc(doc(db, 'alerts', alert.id), { enabled: nextEnabled });
      fetchUserAlerts(user);
      addNotification('success', t.alertUpdated);
    } catch (error) {
      addNotification('error', t.genericError.replace('{message}', error.message));
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setStep(1);
    setActiveTab('search');
    setFormData({ keyword: '', location: '', qualifiers: [], disqualifiers: [] });
    setIsValidLocationSelected(false);
    setEmail('');
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!newEmail) return;

    // Check if user signed in recently (within 5 minutes)
    const recentlySignedIn = lastSignInTimeRef.current && (Date.now() - lastSignInTimeRef.current) < 5 * 60 * 1000;

    // Try updateEmail directly if they signed in recently
    if (recentlySignedIn) {
      try {
        await updateEmail(user, newEmail);
        addNotification('success', t.emailUpdated);
        setNewEmail('');
        if (analytics) {
          logEvent(analytics, 'email_updated');
        }
        // Update the last sign-in time since we just performed a sensitive operation
        lastSignInTimeRef.current = Date.now();
        return;
      } catch (error) {
        // If updateEmail fails, fall through to verification flow
        console.log('Direct updateEmail failed, trying verification:', error);
      }
    }

    // Use verification flow (either because not recent sign-in or updateEmail failed)
    try {
      await verifyBeforeUpdateEmail(user, newEmail, { url: window.location.origin });
      addNotification('success', t.emailVerificationSent.replace('{email}', newEmail));
      setNewEmail('');
    } catch (error) {
      const requiresRecentLogin =
        error?.code === 'auth/requires-recent-login' ||
        (error?.message && error.message.includes('CREDENTIAL_TOO_OLD_LOGIN_AGAIN'));
      if (requiresRecentLogin && user?.email) {
        try {
          const reauthUrl = new URL(window.location.href);
          reauthUrl.searchParams.set('email', user.email);
          const reauthLiFatId = window.localStorage.getItem(LI_FAT_ID_KEY);
          if (reauthLiFatId) reauthUrl.searchParams.set('li_fat_id', reauthLiFatId);
          const actionCodeSettings = { url: reauthUrl.toString(), handleCodeInApp: true };
          await sendSignInLinkToEmail(auth, user.email, actionCodeSettings);
          window.localStorage.setItem('emailForSignIn', user.email);
          addNotification('error', t.reauthRequired);
          addNotification('success', t.reauthLinkSent.replace('{email}', user.email));
        } catch (e2) {
          addNotification('error', t.genericError.replace('{message}', e2.message));
        }
      } else {
        addNotification('error', t.errorUpdatingEmail.replace('{message}', error.message));
      }
    }
  };

  const handleDeleteAccount = async () => {
    openConfirm(t.confirmDeleteAccountTitle, t.confirmDeleteAccountMessage, async () => {
      try {
        await deleteUser(user);
        addNotification('success', t.accountDeleted);
        if (analytics) {
          logEvent(analytics, 'account_deleted');
        }
        setStep(1);
        setActiveTab('search');
        setFormData({ keyword: '', location: '', qualifiers: [], disqualifiers: [] });
        setIsValidLocationSelected(false);
        setEmail('');
      } catch (error) {
        addNotification('error', t.errorDeletingAccount.replace('{message}', error.message));
      }
    });
  };

  const fetchLocationSuggestions = (query) => {
    if (!query || query.length < 2) {
      setLocationSuggestions([]);
      setShowLocationDropdown(false);
      return;
    }

    fetchGooglePredictions(query, lang, (results) => {
      setLocationSuggestions(results);
      if (results.length > 0) {
        updateDropdownPosition();
        setShowLocationDropdown(true);
      } else {
        setShowLocationDropdown(false);
      }
    });
  };

  const updateDropdownPosition = () => {
    if (locationInputRef.current) {
      const rect = locationInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top, // Will position dropdown above
        left: rect.left,
        width: rect.width
      });
    }
  };

  const handleLocationInput = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, location: value });
    setIsValidLocationSelected(false);
    updateDropdownPosition();

    // Clear previous timeout
    if (locationSearchTimeout) {
      clearTimeout(locationSearchTimeout);
    }

    // Debounce the API call
    const timeout = setTimeout(() => {
      fetchLocationSuggestions(value);
    }, 300);

    setLocationSearchTimeout(timeout);
  };

  const handleLocationSelect = (suggestion) => {
    setFormData({
      ...formData,
      location: suggestion.description
    });
    setIsValidLocationSelected(true);
    setShowLocationDropdown(false);
    setLocationSuggestions([]);

    if (analytics) {
      logEvent(analytics, 'location_selected', {
        city: suggestion.mainText,
        full_name: suggestion.description
      });
    }
  };

  const handleSearch = async () => {
    // Validate location selection
    if (!isValidLocationSelected && formData.location) {
      addNotification('error', t.selectLocationFromList || 'Please select a location from the suggestions');
      return;
    }

    // Skip results page; go straight to alert creation/signup
    setLoading(true);
    try {
      if (user) {
        if (userAlerts.length >= 3) {
          addNotification('error', t.maxAlertsReached);
        } else {
          const batch = writeBatch(db);
          const alertRef = doc(collection(db, 'alerts'));
          batch.set(alertRef, {
            uid: user.uid,
            keyword: formData.keyword,
            location: formData.location,
            qualifiers: formData.qualifiers.filter(q => q.trim() !== ''),
            disqualifiers: formData.disqualifiers.filter(d => d.trim() !== ''),
            language: lang,
            enabled: true,
            createdAt: new Date()
          });
          await batch.commit();

          addNotification('success', t.alertCreated);
          if (analytics) {
            logEvent(analytics, 'alert_created', {
              keyword: formData.keyword,
              location: formData.location,
              qualifiers_count: formData.qualifiers.filter(q => q.trim() !== '').length,
              disqualifiers_count: formData.disqualifiers.filter(d => d.trim() !== '').length,
              language: lang
            });
          }
          // Reset search form and step for a clean home page
          setFormData({ keyword: '', location: '', qualifiers: [], disqualifiers: [] });
          setIsValidLocationSelected(false);
          setStep(1);
          fetchUserAlerts(user);
          setActiveTab('alerts');
        }
      } else {
        setShowSignup(true);
        setStep(3);
      }
    } catch (error) {
      addNotification('error', t.errorCreatingAlert.replace('{message}', error.message));
    } finally {
      setLoading(false);
    }
  };

  // Removed unused handleAlertClick

  const handleSignup = async (e) => {
    e.preventDefault();
    const alertPayload = JSON.stringify({
      keyword: formData.keyword,
      location: formData.location,
      qualifiers: formData.qualifiers.filter(q => q.trim() !== ''),
      disqualifiers: formData.disqualifiers.filter(d => d.trim() !== ''),
      language: lang,
    });
    const signupUrl = new URL(window.location.origin);
    signupUrl.searchParams.set('pendingAlert', btoa(alertPayload));
    signupUrl.searchParams.set('email', email);
    const storedLiFatId = window.localStorage.getItem(LI_FAT_ID_KEY);
    if (storedLiFatId) signupUrl.searchParams.set('li_fat_id', storedLiFatId);
    const actionCodeSettings = {
      url: signupUrl.toString(),
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);

      addNotification('success', t.loginLinkSent.replace('{email}', email));
      setEmailForConfirmation(email);
      setEmailLinkSent(true);
      setShowSignup(false);
      if (analytics) {
        logEvent(analytics, 'sign_up', { method: 'email_link' });
      }
    } catch (error) {
      addNotification('error', t.genericError.replace('{message}', error.message));
    }
  };

  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    const signInUrl = new URL(window.location.href);
    signInUrl.searchParams.set('email', email);
    const signInLiFatId = window.localStorage.getItem(LI_FAT_ID_KEY);
    if (signInLiFatId) signInUrl.searchParams.set('li_fat_id', signInLiFatId);
    const actionCodeSettings = {
      url: signInUrl.toString(),
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      addNotification('success', t.loginLinkSent.replace('{email}', email));
      setShowSignIn(false);
      setEmail('');
      if (analytics) {
        logEvent(analytics, 'login_link_requested', { method: 'email_link' });
      }
    } catch (error) {
      addNotification('error', t.genericError.replace('{message}', error.message));
    }
  };

  // --- COMPONENT RENDERING ---
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center relative px-4">
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 left-4 sm:left-auto z-50 space-y-2 max-w-sm sm:max-w-none">
        {notifications.map((n) => (
          <div key={n.id} className={`flex items-start gap-3 bg-white rounded-xl shadow-xl p-4 border-l-4 ${n.type === 'error' ? 'border-red-600' : n.type === 'success' ? 'border-blue-600' : 'border-gray-600'}`}>
            <div className={`text-sm ${n.type === 'error' ? 'text-red-700' : 'text-gray-700'}`}>{n.message}</div>
            <button onClick={() => removeNotification(n.id)} className="ml-auto text-gray-400 hover:text-gray-600">✕</button>
          </div>
        ))}
      </div>
      {/* HEADER */}
      <header className="absolute top-0 w-full p-6 flex justify-between items-center">
        <button
          onClick={() => {
            setActiveTab('search');
            setStep(1);
            setFormData({ keyword: '', location: '', qualifiers: [], disqualifiers: [] });
            setIsValidLocationSelected(false);
            setShowSignup(false);
            setShowSignIn(false);
            setEmailLinkSent(false);
            setEmailForConfirmation('');
          }}
          className="cursor-pointer transition hover:opacity-80"
        >
          <img
            src="/logo.png"
            alt="Jobo.bot"
            className="h-16 w-auto"
          />
        </button>

        <div className="flex items-center gap-4">
          {user ? (
            <div className="flex gap-2">
              <button
                onClick={() => handleTabChange('search')}
                className={`px-3 py-1 rounded-full transition ${activeTab === 'search' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {t.search}
              </button>
              <button
                onClick={() => handleTabChange('alerts')}
                className={`px-3 py-1 rounded-full transition ${activeTab === 'alerts' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {t.myAlerts}
              </button>
              <button
                onClick={() => handleTabChange('faqs')}
                className={`px-3 py-1 rounded-full transition ${activeTab === 'faqs' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {t.faqs}
              </button>
            </div>
          ) : (
            <div className="flex gap-2 items-center">
              <button
                onClick={() => handleTabChange('faqs')}
                className={`px-3 py-1 rounded-full transition ${activeTab === 'faqs' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {t.faqs}
              </button>
              <button
                onClick={() => setShowSignIn(true)}
                className="text-sm font-semibold text-blue-600 hover:text-blue-800"
              >
                {t.signIn}
              </button>
            </div>
          )}

          <button
            onClick={toggleLanguage}
            className="text-sm text-gray-500 uppercase hover:text-blue-600 font-bold cursor-pointer"
          >
            {lang}
          </button>
        </div>
      </header>

      {/* MAIN CARD CONTAINER */}
      <main className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 mt-28">

        {/* PROGRESS BAR (Only show in search flow) */}
        {activeTab === 'search' && (
          <div className="h-1 bg-gray-100 w-full">
            <div
              className="h-full bg-blue-500 transition-all duration-500"
              style={{ width: `${(step / 3) * 100}%` }}
            ></div>
          </div>
        )}

        <div className="p-8">

          {/* SIGN IN FORM */}
          {showSignIn && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">{t.signInTitle}</h2>
                <button onClick={() => setShowSignIn(false)} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
              <form onSubmit={handleSignInSubmit} className="space-y-4">
                <input
                  type="email"
                  required
                  placeholder={t.emailPH}
                  className="w-full p-4 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-lg shadow-lg transform transition hover:-translate-y-1">
                  {t.signInBtn}
                </button>
              </form>
            </div>
          )}

          {/* UNSUBSCRIBE TAB */}
          {activeTab === 'unsubscribe' && (
            <div className="space-y-6 animate-fade-in">
              {unsubscribeStatus === 'loading' && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">{t.loading}</p>
                </div>
              )}
              {unsubscribeStatus === 'success' && (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{t.unsubscribeSuccess}</h2>
                  <button
                    onClick={() => {
                      setActiveTab('search');
                      setUnsubscribeStatus(null);
                    }}
                    className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    {t.search}
                  </button>
                </div>
              )}
              {unsubscribeStatus === 'error' && (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{t.unsubscribeError.replace('{message}', '')}</h2>
                  <button
                    onClick={() => {
                      setActiveTab('search');
                      setUnsubscribeStatus(null);
                    }}
                    className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    {t.returnToHomePage}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* FAQs TAB */}
          {!showSignIn && activeTab === 'faqs' && (
            <div className="space-y-6 animate-fade-in pt-6">
              <h2 className="text-2xl font-bold text-gray-800">{t.faqsTitle}</h2>
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t.faq1Question}</h3>
                  <p className="text-gray-600">{t.faq1Answer}</p>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t.faq2Question}</h3>
                  <p className="text-gray-600">{t.faq2Answer}</p>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t.faq3Question}</h3>
                  <p className="text-gray-600">{t.faq3Answer}</p>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t.faq4Question}</h3>
                  <p className="text-gray-600">{t.faq4Answer}</p>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t.faq5Question}</h3>
                  <p className="text-gray-600">{t.faq5Answer}</p>
                </div>
                <div className="pb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t.faq6Question}</h3>
                  <p className="text-gray-600">{t.faq6Answer}</p>
                </div>
              </div>
            </div>
          )}

          {/* ALERTS TAB */}
          {!showSignIn && activeTab === 'alerts' && (
            <div className="space-y-6 animate-fade-in">
              <h2 className="text-2xl font-bold text-gray-800">{t.myAlerts}</h2>
              {userAlerts.length === 0 ? (
                <p className="text-gray-500 text-center py-8">{t.noAlerts}</p>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
                  {userAlerts.map((alert) => (
                    <div key={alert.id}>
                      <div
                        onClick={() => handleAlertClick(alert.id)}
                        className="p-4 border border-gray-100 rounded-lg bg-gray-50 relative group cursor-pointer hover:bg-gray-100 transition"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-800">{alert.keyword}</h3>
                            <p className="text-sm text-gray-600">{alert.location}</p>
                            {alert.requisites && (
                              <p className="text-xs text-gray-500 mt-1 truncate">{alert.requisites}</p>
                            )}
                            {alert.qualifiers && alert.qualifiers.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {alert.qualifiers.map((q, i) => (
                                  <span key={`aq-${i}`} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">{q}</span>
                                ))}
                              </div>
                            )}
                            {alert.disqualifiers && alert.disqualifiers.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {alert.disqualifiers.map((d, i) => (
                                  <span key={`ad-${i}`} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">{d}</span>
                                ))}
                              </div>
                            )}
                            <div className="mt-3 flex items-center gap-3">
                              <label className="text-xs text-gray-600">{t.enabledLabel}</label>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAlertEnabled(alert);
                                }}
                                className={`w-10 h-6 rounded-full transition relative ${(alert.enabled !== false) ? 'bg-blue-600' : 'bg-gray-300'}`}
                                aria-pressed={(alert.enabled !== false)}
                                title={t.enabledLabel}
                              >
                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition ${(alert.enabled !== false) ? 'translate-x-4' : ''}`}></span>
                              </button>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAlert(alert.id);
                            }}
                            className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition ml-4"
                            title={t.delete}
                          >
                            {t.delete}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(alert);
                            }}
                            className="text-blue-600 hover:text-blue-800 opacity-0 group-hover:opacity-100 transition ml-2"
                            title={t.edit}
                          >
                            {t.edit}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}


              {/* Sign out moved to Account Settings card */}
            </div>
          )}

          {/* STEP 1: LANDING (Keyword + Location) */}
          {!showSignIn && activeTab === 'search' && step === 1 && (
            <div className="space-y-6 animate-fade-in-up">
              <h2 className="text-2xl font-bold text-gray-800">{t.searchTitle}</h2>
              <p className="text-sm text-gray-500">{t.homeIntro}</p>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder={t.keywordPH}
                  className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.keyword}
                  onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
                />
                <div className="relative location-autocomplete">
                  <input
                    ref={locationInputRef}
                    type="text"
                    placeholder={t.locationPH}
                    className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.location}
                    onChange={handleLocationInput}
                    onFocus={() => {
                      updateDropdownPosition();
                      if (locationSuggestions.length > 0) {
                        setShowLocationDropdown(true);
                      }
                    }}
                    autoComplete="off"
                  />
                  {showLocationDropdown && locationSuggestions.length > 0 && (
                    <div
                      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                      style={{
                        top: `${Math.max(10, dropdownPosition.top - 240)}px`, // Position above input, min 10px from top
                        left: `${dropdownPosition.left}px`,
                        width: `${dropdownPosition.width}px`
                      }}
                    >
                      {locationSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-3 hover:bg-blue-50 cursor-pointer transition"
                          onClick={() => handleLocationSelect(suggestion)}
                        >
                          <div className="font-medium text-gray-800">{suggestion.mainText}</div>
                          {suggestion.secondaryText && (
                            <div className="text-sm text-gray-500">
                              {suggestion.secondaryText}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={!formData.keyword || !formData.location || !isValidLocationSelected}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg transition disabled:opacity-50"
              >
                {t.next}
              </button>
            </div>
          )}

          {/* STEP 2: FILTERS (Qualifying/Disqualifying Lists) */}
          {!showSignIn && activeTab === 'search' && step === 2 && (
            <div className="space-y-6 animate-fade-in-up">
              <button onClick={() => setStep(1)} className="text-sm text-gray-400 hover:text-gray-600">← {t.back}</button>
              <h2 className="text-2xl font-bold text-gray-800">{t.filtersTitle}</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-800">{t.qualifiersTitle}</h3>
                  <div className="space-y-2 mt-2">
                    {formData.qualifiers.length === 0 && (
                      <p className="text-xs text-gray-400">{t.qualifierPH}</p>
                    )}
                    {formData.qualifiers.map((q, i) => (
                      <div key={`q-${i}`} className="flex gap-2">
                        <input
                          type="text"
                          value={q}
                          onChange={(e) => updateQualifier(i, e.target.value)}
                          className="flex-1 p-3 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={t.qualifierPH}
                        />
                        <button
                          type="button"
                          onClick={() => removeQualifier(i)}
                          className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
                        >
                          {t.removeItem}
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addQualifier}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    + {t.addItem}
                  </button>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800">{t.disqualifiersTitle}</h3>
                  <div className="space-y-2 mt-2">
                    {formData.disqualifiers.length === 0 && (
                      <p className="text-xs text-gray-400">{t.disqualifierPH}</p>
                    )}
                    {formData.disqualifiers.map((d, i) => (
                      <div key={`d-${i}`} className="flex gap-2">
                        <input
                          type="text"
                          value={d}
                          onChange={(e) => updateDisqualifier(i, e.target.value)}
                          className="flex-1 p-3 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={t.disqualifierPH}
                        />
                        <button
                          type="button"
                          onClick={() => removeDisqualifier(i)}
                          className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
                        >
                          {t.removeItem}
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addDisqualifier}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    + {t.addItem}
                  </button>
                </div>
              </div>

              <button
                onClick={handleSearch}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg transition flex justify-center items-center"
              >
                {loading ? (
                  <span className="animate-pulse">{t.loading}</span>
                ) : t.searchBtn}
              </button>
            </div>
          )}

          {/* STEP 3 is now only the signup form (shown below) */}

          {/* STEP 3: SIGNUP FORM */}
          {!showSignIn && activeTab === 'search' && step === 3 && showSignup && !emailLinkSent && (
            <div className="space-y-6 animate-fade-in-up">
              <h2 className="text-xl font-bold text-gray-800">{t.signupTitle}</h2>
              <p className="text-sm text-gray-500 mt-2">
                {t.notifyPrefix} {t.notifySuffix}
              </p>

              <form onSubmit={handleSignup} className="space-y-4">
                <input
                  type="email"
                  required
                  placeholder={t.emailPH}
                  className="w-full p-4 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <label className="flex items-start gap-2 text-sm text-gray-600 cursor-not-allowed">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled={true}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-not-allowed"
                    readOnly
                  />
                  <span>{t.emailConsent}</span>
                </label>
                <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-lg shadow-lg transform transition hover:-translate-y-1">
                  {t.signupBtn}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSignup(false);
                  }}
                  className="w-full text-gray-400 text-sm hover:text-gray-600"
                >
                  {t.cancel}
                </button>
              </form>
            </div>
          )}

          {/* STEP 3: EMAIL CONFIRMATION MESSAGE */}
          {!showSignIn && activeTab === 'search' && step === 3 && emailLinkSent && (
            <div className="space-y-6 animate-fade-in-up">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{t.checkEmailTitle}</h2>
                <p className="text-sm text-gray-600 mb-4">
                  {t.checkEmailMessage.replace('{email}', emailForConfirmation)}
                </p>
                <p className="text-xs text-gray-500 italic">
                  {t.checkEmailNote}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEmailLinkSent(false);
                  setEmailForConfirmation('');
                  setShowSignup(false);
                  setStep(1);
                  setFormData({ keyword: '', location: '', qualifiers: [], disqualifiers: [] });
                  setIsValidLocationSelected(false);
                }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg transition"
              >
                {t.back}
              </button>
            </div>
          )}

        </div>
      </main>
      {/* Matches Modal Overlay */}
      {selectedAlertId && (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => handleAlertClick(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">{t.latestMatches}</h3>
              <button
                onClick={() => handleAlertClick(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingMatches ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">{t.loadingMatches}</p>
                </div>
              ) : alertMatches.length === 0 ? (
                <p className="text-gray-500 text-center py-12">{t.noMatches}</p>
              ) : (
                <div className="space-y-4">
                  {alertMatches.map((match) => {
                    const parser = match.parser || {};
                    const props = match.properties || {};
                    const companySlug = match.company_slug || 'default';
                    const logoUrl = parser.company_logo || `https://storage.googleapis.com/jobobot-public/logos/${companySlug}.jpg`;

                    // Build salary string
                    let salary = '';
                    if (parser.min_amount || parser.max_amount) {
                      const currency = parser.currency || '';
                      const min = parser.min_amount ? `${currency}${parser.min_amount}` : '';
                      const max = parser.max_amount ? `${currency}${parser.max_amount}` : '';
                      salary = min && max ? `${min} - ${max}` : (min || max);
                      if (parser.interval) salary += ` / ${parser.interval}`;
                    }

                    const title = parser.title || "Job Title";
                    const company = parser.company || "Company";
                    const location = parser.location || '';
                    const isRemote = parser.is_remote || false;
                    const workFromHomeType = parser.work_from_home_type || '';

                    let locationLabel = location;
                    if (isRemote || workFromHomeType) {
                      const remoteText = workFromHomeType || (isRemote ? t.remote : '');
                      locationLabel = location && remoteText
                        ? `${location} • ${remoteText}`
                        : (location || remoteText);
                    }

                    const jobUrl = parser.job_url || "#";
                    const reason = match.reason || "";
                    const skills = parser.skills || props.skills_required || [];

                    // Calculate time since extracted
                    let timeAgo = '';
                    if (match.extractedAt) {
                      const now = Date.now();
                      const extractedTime = match.extractedAt.toMillis ? match.extractedAt.toMillis() : match.extractedAt;
                      const diffMs = now - extractedTime;
                      const diffMins = Math.floor(diffMs / (1000 * 60));
                      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

                      if (diffMins < 1) {
                        timeAgo = t.seenJustNow;
                      } else if (diffHours < 1) {
                        timeAgo = diffMins === 1 ? t.seenMinute : t.seenMinutes.replace('{count}', diffMins);
                      } else if (diffDays < 1) {
                        timeAgo = diffHours === 1 ? t.seenHour : t.seenHours.replace('{count}', diffHours);
                      } else {
                        timeAgo = diffDays === 1 ? t.seenDay : t.seenDays.replace('{count}', diffDays);
                      }
                    }

                    return (
                      <div key={match.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-start gap-4 mb-3">
                          <img
                            src={logoUrl}
                            alt={company}
                            className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-200 flex-shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="flex-1">
                            <h5 className="font-bold text-gray-800 mb-1">
                              <a href={jobUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                {title}
                              </a>
                            </h5>
                            <p className="text-sm text-gray-600">
                              {company}{locationLabel ? ` • ${locationLabel}` : ''}
                            </p>
                            {timeAgo && (
                              <p className="text-xs text-gray-500 mt-1">
                                {timeAgo}
                              </p>
                            )}
                          </div>
                        </div>
                        {reason && (
                          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded">
                            <p className="text-xs text-green-800">
                              <strong>{t.matchReason}</strong> {reason}
                            </p>
                          </div>
                        )}
                        {salary && (
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>{t.salary}</strong> {salary}
                          </p>
                        )}
                        {skills.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {skills.slice(0, 6).map((skill, idx) => (
                              <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                        <a
                          href={jobUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded hover:bg-blue-700 transition"
                          onClick={() => {
                            if (analytics) {
                              logEvent(analytics, 'job_clicked', {
                                alert_id: selectedAlertId,
                                job_id: match.job_id,
                                job_url: jobUrl
                              });
                            }
                          }}
                        >
                          {t.viewJob}
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal Overlay */}
      {confirmState.open && (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-gray-800">{confirmState.title || 'Confirm'}</h3>
            <p className="text-sm text-gray-600 mt-2">{confirmState.message}</p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeConfirm}
                className="px-4 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                {t.cancel}
              </button>
              <button
                onClick={async () => { try { if (confirmState.onConfirm) await confirmState.onConfirm(); } finally { closeConfirm(); } }}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                {t.delete}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Alert Modal Overlay */}
      {editState.open && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeEdit}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800">{t.edit}</h3>
              <button
                onClick={closeEdit}
                className="text-gray-400 hover:text-gray-600 text-2xl"
                aria-label={t.close}
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <input
                type="text"
                value={editState.keyword}
                onChange={(e) => updateEditField('keyword', e.target.value)}
                placeholder={t.keywordPH}
                className="w-full p-3 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="relative">
                <input
                  ref={editLocationInputRef}
                  type="text"
                  value={editState.location}
                  onChange={handleEditLocationInput}
                  onFocus={() => {
                    if (editLocationSuggestions.length > 0) {
                      setShowEditLocationDropdown(true);
                    }
                  }}
                  placeholder={t.locationPH}
                  className="w-full p-3 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  autoComplete="off"
                />
                {showEditLocationDropdown && editLocationSuggestions.length > 0 && (
                  <div
                    className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto w-full mt-1"
                  >
                    {editLocationSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="p-3 hover:bg-blue-50 cursor-pointer transition"
                        onClick={() => handleEditLocationSelect(suggestion)}
                      >
                        <div className="font-medium text-gray-800">{suggestion.mainText}</div>
                        <div className="text-sm text-gray-500">
                          {suggestion.secondaryText}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">{t.qualifiersTitle}</h4>
                <div className="space-y-2 mt-2">
                  {editState.qualifiers.length === 0 && (
                    <p className="text-xs text-gray-400">{t.qualifierPH}</p>
                  )}
                  {editState.qualifiers.map((q, i) => (
                    <div key={`eq-${i}`} className="flex gap-2">
                      <input
                        type="text"
                        value={q}
                        onChange={(e) => updateEditQualifier(i, e.target.value)}
                        className="flex-1 p-3 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t.qualifierPH}
                      />
                      <button
                        type="button"
                        onClick={() => removeEditQualifier(i)}
                        className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
                      >
                        {t.removeItem}
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addEditQualifier}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  + {t.addItem}
                </button>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">{t.disqualifiersTitle}</h4>
                <div className="space-y-2 mt-2">
                  {editState.disqualifiers.length === 0 && (
                    <p className="text-xs text-gray-400">{t.disqualifierPH}</p>
                  )}
                  {editState.disqualifiers.map((d, i) => (
                    <div key={`ed-${i}`} className="flex gap-2">
                      <input
                        type="text"
                        value={d}
                        onChange={(e) => updateEditDisqualifier(i, e.target.value)}
                        className="flex-1 p-3 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t.disqualifierPH}
                      />
                      <button
                        type="button"
                        onClick={() => removeEditDisqualifier(i)}
                        className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
                      >
                        {t.removeItem}
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addEditDisqualifier}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  + {t.addItem}
                </button>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
              <button
                onClick={closeEdit}
                className="px-4 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleSaveEditedAlert}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                {t.update}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Matches Modal Overlay */}
      {selectedAlertId && (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => handleAlertClick(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">{t.latestMatches}</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{t.showAllResults}</span>
                  <button
                    role="switch"
                    aria-checked={showAllResults}
                    onClick={() => {
                      const nextShowAll = !showAllResults;
                      setShowAllResults(nextShowAll);
                      fetchAlertMatches(selectedAlertId, nextShowAll);
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${showAllResults ? 'bg-blue-600' : 'bg-gray-200'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showAllResults ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
                <button
                  onClick={() => handleAlertClick(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingMatches ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">{t.loadingMatches}</p>
                </div>
              ) : alertMatches.length === 0 ? (
                <p className="text-gray-500 text-center py-12">{t.noMatches}</p>
              ) : (
                <div className="space-y-4">
                  {alertMatches.map((match) => {
                    const parser = match.parser || {};
                    const props = match.properties || {};
                    const companySlug = match.company_slug || 'default';
                    const logoUrl = parser.company_logo || `https://storage.googleapis.com/jobobot-public/logos/${companySlug}.jpg`;

                    // Build salary string
                    let salary = '';
                    if (parser.min_amount || parser.max_amount) {
                      const currency = parser.currency || '';
                      const min = parser.min_amount ? `${currency}${parser.min_amount}` : '';
                      const max = parser.max_amount ? `${currency}${parser.max_amount}` : '';
                      salary = min && max ? `${min} - ${max}` : (min || max);
                      if (parser.interval) salary += ` / ${parser.interval}`;
                    }

                    const title = parser.title || "Job Title";
                    const company = parser.company || "Company";
                    const location = parser.location || '';
                    const isRemote = parser.is_remote || false;
                    const workFromHomeType = parser.work_from_home_type || '';

                    let locationLabel = location;
                    if (isRemote || workFromHomeType) {
                      const remoteText = workFromHomeType || (isRemote ? t.remote : '');
                      locationLabel = location && remoteText
                        ? `${location} • ${remoteText}`
                        : (location || remoteText);
                    }

                    const jobUrl = parser.job_url || "#";
                    const reason = match.reason || "";
                    const skills = parser.skills || props.skills_required || [];

                    // Calculate time since extracted
                    let timeAgo = '';
                    if (match.extractedAt) {
                      const now = Date.now();
                      const extractedTime = match.extractedAt.toMillis ? match.extractedAt.toMillis() : match.extractedAt;
                      const diffMs = now - extractedTime;
                      const diffMins = Math.floor(diffMs / (1000 * 60));
                      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

                      if (diffMins < 1) {
                        timeAgo = t.seenJustNow;
                      } else if (diffHours < 1) {
                        timeAgo = diffMins === 1 ? t.seenMinute : t.seenMinutes.replace('{count}', diffMins);
                      } else if (diffDays < 1) {
                        timeAgo = diffHours === 1 ? t.seenHour : t.seenHours.replace('{count}', diffHours);
                      } else {
                        timeAgo = diffDays === 1 ? t.seenDay : t.seenDays.replace('{count}', diffDays);
                      }
                    }

                    return (
                      <div key={match.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-start gap-4 mb-3">
                          <img
                            src={logoUrl}
                            alt={company}
                            className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-200 flex-shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="flex-1">
                            <h5 className="font-bold text-gray-800 mb-1">
                              <a href={jobUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                {title}
                              </a>
                            </h5>
                            <p className="text-sm text-gray-600">
                              {company}{locationLabel ? ` • ${locationLabel}` : ''}
                            </p>
                            {timeAgo && (
                              <p className="text-xs text-gray-500 mt-1">
                                {timeAgo}
                              </p>
                            )}
                          </div>
                        </div>
                        {(reason) && (
                          <div className={`mb-3 p-3 border rounded ${match.match ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                            <p className={`text-xs ${match.match ? 'text-green-800' : 'text-red-800'}`}>
                              <strong>{match.match ? t.matchReason : t.notMatchReason}</strong> {reason}
                            </p>
                          </div>
                        )}
                        {
                          salary && (
                            <p className="text-sm text-gray-700 mb-2">
                              <strong>{t.salary}</strong> {salary}
                            </p>
                          )
                        }
                        {
                          skills.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {skills.slice(0, 6).map((skill, idx) => (
                                <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          )
                        }
                        <a
                          href={jobUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded hover:bg-blue-700 transition"
                          onClick={() => {
                            if (analytics) {
                              logEvent(analytics, 'job_clicked', {
                                alert_id: selectedAlertId,
                                job_id: match.job_id,
                                job_url: jobUrl
                              });
                            }
                          }}
                        >
                          {t.viewJob}
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ACCOUNT SETTINGS SECTION (outside Alerts card) */}
      {
        user && activeTab === 'alerts' && (
          <section className="w-full max-w-md mt-6">
            <div className="p-4 border border-gray-200 rounded-2xl bg-white shadow-xl">
              <h3 className="font-bold text-gray-800 mb-4">{t.accountSettings}</h3>

              <form onSubmit={handleUpdateEmail} className="space-y-3 mb-6">
                <label className="block text-sm text-gray-600">{t.changeEmail}</label>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t.currentEmailLabel}</p>
                    <input
                      type="email"
                      value={user.email || ''}
                      disabled
                      className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t.newEmailLabel}</p>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder={t.newEmailPH}
                        className="flex-1 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                      />
                      <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition">
                        {t.update}
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
                <button
                  onClick={handleSignOut}
                  className="text-gray-600 text-sm hover:text-gray-800 font-medium"
                >
                  {t.signOut}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="text-red-500 text-sm hover:text-red-700 font-medium"
                >
                  {t.deleteAccount}
                </button>
              </div>
            </div>
          </section>
        )
      }

      {/* Footer */}
      <footer className="mt-16 pt-8 pb-8 border-t border-gray-200">
        <div className="flex justify-center items-center gap-6 text-sm text-gray-600">
          <a
            href="https://storage.googleapis.com/jobobot-public/docs/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-900 transition-colors"
          >
            {t.privacyPolicy}
          </a>
          <span className="text-gray-300">•</span>
          <a
            href="https://storage.googleapis.com/jobobot-public/docs/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-900 transition-colors"
          >
            {t.termsOfUse}
          </a>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      {
        showCookieConsent && (
          <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 shadow-lg z-50">
            <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-center sm:text-left">
                {t.cookieConsent}{' '}
                <a
                  href="https://storage.googleapis.com/jobobot-public/docs/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-300"
                >
                  {t.privacyPolicy}
                </a>
                {' '}{t.cookieConsentAnd}{' '}
                <a
                  href="https://storage.googleapis.com/jobobot-public/docs/terms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-300"
                >
                  {t.termsOfUse}
                </a>.
              </p>
              <button
                onClick={handleAcceptCookies}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium whitespace-nowrap transition-colors"
              >
                {t.cookieAccept}
              </button>
            </div>
          </div>
        )
      }

      {/* Marketing Consent Prompt */}
      {
        showMarketingPrompt && user && (
          <div
            className="fixed left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-40"
            style={{ bottom: showCookieConsent ? '80px' : '0' }}
          >
            <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-700 text-center sm:text-left">
                {t.marketingPrompt}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleMarketingPromptYes}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium whitespace-nowrap transition-colors"
                >
                  {t.marketingPromptYes}
                </button>
                <button
                  onClick={handleMarketingPromptNo}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium whitespace-nowrap transition-colors"
                >
                  {t.marketingPromptNo}
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default App;